import { useEffect, useMemo, useState } from "preact/hooks";
import type { AppSettings, Folder, PushInfo } from "./api";
import { api } from "./api";
import { relativeTime } from "./format";
import { Icon, folderIcon } from "./icons";
import { isNetworkError } from "./net";
import { saveOtherSettings } from "./offline";
import { type PushSupport, PushRefused, notifyFolder, pushFolders, pushSupport, thisDevice, turnOff, turnOn } from "./push";
import { type FolderNode, folderTree } from "./tree";

interface Props {
  folders: Folder[];
  settings: AppSettings | null;
  online: boolean;
  onSaved: (settings: AppSettings) => void;
  onMenu: () => void;
  onError: (error: unknown) => void;
  onNotice: (text: string) => void;
}

interface Here {
  support: PushSupport;
  permission: NotificationPermission | null;
  /** The id of this device's push subscription, whether the server knows it or not. */
  id: string | null;
}

async function here(): Promise<Here> {
  const support = pushSupport();
  if (support !== "supported") return { support, permission: null, id: null };
  return { support, permission: Notification.permission, id: (await thisDevice())?.id ?? null };
}

/** Whether new mail is notified on this device (turned on and off here), in which folders (the same on every device,
 * stored on the server), and the devices notified, each with how its last notification went. */
export function NotificationSettings({ folders, settings, online, onSaved, onMenu, onError, onNotice }: Props) {
  const [device, setDevice] = useState<Here | null>(null);
  const [info, setInfo] = useState<PushInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  /** The folders being saved, shown at once (put back if saving fails). */
  const [pending, setPending] = useState<string[] | null>(null);
  const tree = useMemo(() => folderTree(folders), [folders]);

  const load = async () => {
    setDevice(await here());
    try {
      setInfo(await api.push());
    } catch (e) {
      if (!isNetworkError(e)) onError(e);
    }
  };
  useEffect(() => {
    load();
  }, [online]);

  const act = async (what: () => Promise<unknown>, done?: string) => {
    setBusy(true);
    setProblem(null);
    try {
      await what();
      if (done) onNotice(done);
    } catch (e) {
      if (e instanceof PushRefused) setProblem(e.message);
      else onError(e);
    } finally {
      setBusy(false);
      await load();
    }
  };

  const save = async (next: string[]) => {
    setPending(next);
    try {
      onSaved(await saveOtherSettings({ push_folders: next }));
    } catch (e) {
      onError(e);
    } finally {
      setPending(null);
    }
  };

  const chosen = pending ?? pushFolders(settings);
  const notified = (name: string) => chosen.some((f) => f.toLowerCase() === name.toLowerCase());
  const known = info?.devices.find((d) => d.id === device?.id) ?? null;
  const on = known !== null && device?.permission === "granted";
  const others = info?.devices.filter((d) => d.id !== device?.id) ?? [];

  const rows = (nodes: FolderNode[]): preact.JSX.Element[] =>
    nodes.flatMap((n) => [
      <li key={n.path} class="offline-folder" style={{ "--depth": n.depth }}>
        {n.folder ? (
          <label class="check">
            <input
              type="checkbox"
              checked={notified(n.path)}
              disabled={!online || pending !== null || !settings}
              onChange={(e) => save(notifyFolder(chosen, n.path, (e.target as HTMLInputElement).checked))}
            />
            <Icon name={folderIcon(n.path)} size={18} />
            <span class="folder-name">{n.label}</span>
            {n.folder.unread > 0 && <span class="muted small">{n.folder.unread.toLocaleString()} unread</span>}
          </label>
        ) : (
          <div class="check">
            <Icon name="folder" size={18} />
            <span class="folder-name muted">{n.label}</span>
          </div>
        )}
      </li>,
      ...rows(n.children),
    ]);

  return (
    <div class="offline-settings">
      <div class="toolbar">
        <button class="icon-button menu" title="Folders" onClick={onMenu}>
          <Icon name="menu" />
        </button>
        <h2 class="title">Notifications</h2>
      </div>
      <div class="settings-body">
        <p class="muted">
          New mail in the folders chosen below is shown as a notification on the devices where notifications are turned on, within about a minute of
          arriving. Mail read before then is left out. The app's icon shows how much mail in those folders is unread.
        </p>
        {!online && <p class="warn-text">No connection: notifications can be changed once there is one.</p>}

        <section class="storage" aria-label="This device">
          <strong>This device</strong>
          {device === null ? (
            <span class="muted">…</span>
          ) : device.support === "install-first" ? (
            <span>
              On the iPhone and iPad, notifications come to the app on the home screen: open this page in Safari, tap Share and then Add to Home
              Screen, and turn them on in the app.
            </span>
          ) : device.support === "unsupported" ? (
            <span>This browser cannot show notifications from the app.</span>
          ) : device.permission === "denied" ? (
            <span class="warn-text">
              Notifications are blocked for this app. Allow them in the device's settings (on the iPhone: Settings, Notifications, Mail; elsewhere the
              site's settings in the browser), then come back here.
            </span>
          ) : on ? (
            <>
              <span>On: new mail is shown on this device.</span>
              {known.failures > 0 && (
                <span class="warn-text small">
                  The last notification failed ({relativeTime(known.last_error_at)}): {known.last_error}
                </span>
              )}
              <span class="device-actions">
                <button class="text-button" disabled={busy || !online} onClick={() => act(() => api.testPush(known.id), "Test notification sent")}>
                  Send a test
                </button>
                <button class="text-button" disabled={busy || !online} onClick={() => act(turnOff, "Notifications turned off on this device")}>
                  Turn off
                </button>
              </span>
            </>
          ) : (
            <>
              <span class="muted">Off on this device.</span>
              <span class="device-actions">
                <button class="primary" disabled={busy || !online || !info} onClick={() => act(() => turnOn(info!.public_key), "Notifications turned on")}>
                  Turn on notifications
                </button>
              </span>
            </>
          )}
          {problem && <span class="warn-text small">{problem}</span>}
        </section>

        <h3 class="settings-heading">Notify about new mail in</h3>
        <ul class="offline-folders" role="list">
          {rows(tree)}
        </ul>

        <h3 class="settings-heading">Other devices</h3>
        {others.length === 0 ? (
          <p class="muted small">{info ? "No other device gets notifications." : "…"}</p>
        ) : (
          <ul class="device-list" role="list">
            {others.map((d) => (
              <li key={d.id}>
                <div>
                  <div>{d.name ?? d.service ?? d.id}</div>
                  <div class="muted small">
                    Added {relativeTime(d.created_at)}
                    {d.last_sent_at && ` · last notified ${relativeTime(d.last_sent_at)}`}
                  </div>
                  {d.failures > 0 && <div class="warn-text small">Failing: {d.last_error}</div>}
                </div>
                <button
                  class="text-button"
                  disabled={busy || !online}
                  onClick={() => act(() => api.removePushDevice(d.id), `${d.name ?? "The device"} no longer gets notifications`)}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
